import { isApiError, refusalMessageOf, soldCountOf } from "@/data/api/client";
import { czechPlural } from "@/lib/czech";

const seatForms = { one: "místo", few: "místa", many: "míst" } as const;

function seats(count: number): string {
  return `${count} ${czechPlural(count, seatForms)}`;
}

export function timeAndPriceLockedMessage(sold: number): string {
  return `Termín ani cenu už změnit nelze — prodali jste ${seats(sold)}. Potřebujete-li jiný čas nebo cenu, zrušte tento slevový termín a vypište nový.`;
}

/** Said without a number: naming one is exactly what the sold lock does, and
 *  the two must not read as the same answer. */
export const unknownSalesMessage =
  "Kolik míst je prodaných zatím nevíme — termín ani cenu proto teď měnit nelze. Zkuste to prosím za chvíli znovu.";

const heldSeatsPhrase = (error: unknown): string => {
  const sold = soldCountOf(error);
  return sold === null ? "na slevovém termínu už někdo drží místo" : `někdo mezitím drží ${seats(sold)}`;
};

/** The API is the rule and the editor only explains it, so a sale landing
 *  between the screen loading and the save still reaches the partner as an
 *  answer rather than a crash. The hour has an answer of its own, said where it
 *  is refused, because by then the offer's own save has already landed.
 *  `SALE_CLOSES_AFTER_START` is worded in Czech by the API, so it is shown as
 *  sent rather than given a second sentence that could disagree with it. */
export function salesRefusalMessage(error: unknown): string | null {
  if (isApiError(error, "OFFER_HAS_SALES")) {
    const sold = soldCountOf(error);
    const soldPhrase = sold === null ? "slevový termín už někdo koupil" : `prodali jste ${seats(sold)}`;
    return `Cenu jsme neuložili — ${soldPhrase}. Potřebujete-li jinou cenu, zrušte tento slevový termín a vypište nový.`;
  }
  if (isApiError(error, "SALE_CLOSES_AFTER_START")) return error.message;
  return refusalMessageOf(error);
}

/** Moving the hour is a second save that follows the offer's own one, and the
 *  two are not a transaction — so every refusal here arrives with the price and
 *  the selling time already stored, and says both halves. */
export function slotMoveRefusalMessage(error: unknown): string {
  const savedWithoutTheHour = "Cenu a dobu prodeje jsme uložili, termín ne";
  if (isApiError(error, "SLOT_HAS_SALES")) {
    return `${savedWithoutTheHour} — ${heldSeatsPhrase(error)}. Chcete-li jiný čas, zrušte tento slevový termín a vypište nový.`;
  }
  if (isApiError(error, "SLOT_HAS_LIVE_CHECKOUT")) {
    return `${savedWithoutTheHour} — někdo právě dokončuje nákup místa. Zkuste prosím čas přesunout za pár minut znovu.`;
  }
  return `${savedWithoutTheHour}. Zkuste prosím čas přesunout znovu.`;
}

/** The capacity is sent on its own from the list, so the same code means only
 *  one thing there and the partner is not told about a termín they never
 *  touched. */
export function capacityRefusalMessage(error: unknown): string | null {
  if (!isApiError(error, "SLOT_HAS_SALES")) return null;
  return `Počet míst jsme neuložili — ${heldSeatsPhrase(error)}. Míň jich nabídnout nelze.`;
}
